import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  login as apiLogin,
  logout as apiLogout,
  verifyMfa as apiVerifyMfa,
  api,
  refreshSession,
  setAccessToken,
  setUnauthenticatedHandler,
} from './api';
import type { LoginResult, Privilege, PrivilegeScope, SessionUser } from './types';

interface AuthState {
  user: SessionUser | null;
  /** True until the initial silent refresh has settled. */
  loading: boolean;
  /** Set when the session dropped out from under the user mid-work. */
  expired: boolean;
  signIn: (email: string, password: string, remember: boolean) => Promise<LoginResult>;
  completeMfa: (challengeId: string, code: string, remember: boolean) => Promise<LoginResult>;
  signOut: () => Promise<void>;
  /** Re-reads /auth/me — used after enrolling a factor changes what is allowed. */
  refreshUser: () => Promise<void>;
  /** True while the account holds no second factor: every screen is blocked. */
  needsMfaSetup: boolean;
  /** The scope at which the user holds a privilege, or 'none'. */
  scopeOf: (privilege: Privilege) => PrivilegeScope;
  /** True when the privilege is held at any usable scope. */
  can: (privilege: Privilege) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Idle timeout. The design is explicit that any account which can open a
 * merchant application times out at 30 minutes and that an office cannot
 * extend it, so the value comes from the server and is not configurable here.
 */
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const idleMinutes = useRef(30);
  const idleTimer = useRef<number | undefined>(undefined);

  const dropSession = useCallback((wasExpiry: boolean) => {
    setAccessToken(null);
    setUser(null);
    setExpired(wasExpiry);
  }, []);

  // A failed refresh anywhere in the app lands here.
  useEffect(() => {
    setUnauthenticatedHandler(() => dropSession(true));
    return () => setUnauthenticatedHandler(null);
  }, [dropSession]);

  const applySession = useCallback((result: LoginResult): LoginResult => {
    if (result.status === 'ok') {
      setAccessToken(result.accessToken);
      setUser(result.user);
      setExpired(false);
      idleMinutes.current = result.idleTimeoutMinutes;
    }
    return result;
  }, []);

  // On first load, try the refresh cookie before showing the sign-in screen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await refreshSession();
      if (cancelled) return;
      if (ok) {
        try {
          const me = await api.get<SessionUser>('/auth/me');
          if (!cancelled) setUser(me);
        } catch {
          if (!cancelled) setAccessToken(null);
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Idle timeout, reset by real interaction.
  useEffect(() => {
    if (!user) return undefined;

    const reset = () => {
      window.clearTimeout(idleTimer.current);
      idleTimer.current = window.setTimeout(
        () => {
          void apiLogout().catch(() => undefined);
          dropSession(true);
        },
        idleMinutes.current * 60_000,
      );
    };

    reset();
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, reset, { passive: true });
    }
    return () => {
      window.clearTimeout(idleTimer.current);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, reset);
    };
  }, [user, dropSession]);

  const signIn = useCallback(
    async (email: string, password: string, remember: boolean) =>
      applySession(await apiLogin(email, password, remember)),
    [applySession],
  );

  const completeMfa = useCallback(
    async (challengeId: string, code: string, remember: boolean) =>
      applySession(await apiVerifyMfa(challengeId, code, remember)),
    [applySession],
  );

  const refreshUser = useCallback(async () => {
    try {
      setUser(await api.get<SessionUser>('/auth/me'));
    } catch {
      // A failure here is handled by the usual unauthenticated path.
    }
  }, []);

  const signOut = useCallback(async () => {
    await apiLogout().catch(() => undefined);
    dropSession(false);
  }, [dropSession]);

  const value = useMemo<AuthState>(() => {
    const scopeOf = (p: Privilege): PrivilegeScope => user?.privileges[p] ?? 'none';
    return {
      user,
      loading,
      expired,
      signIn,
      completeMfa,
      signOut,
      refreshUser,
      needsMfaSetup: Boolean(user && user.mfa && !user.mfa.enabled),
      scopeOf,
      can: (p) => scopeOf(p) !== 'none',
    };
  }, [user, loading, expired, signIn, completeMfa, signOut, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider.');
  return ctx;
}
